/**
 * 一覧表(設計書 §4)。業務ロジックは持たない(並べ替え・ページ送り・行の選択の表示だけ)。
 *  - 行高 44px(compact 36px)・見出し 40px 固定・横罫線のみ・数値列は右揃え＋等幅数字
 *  - 並べ替えは見出しのボタン。現在の列に aria-sort
 *  - 50件／100件のページ送り(仮想スクロールは使わない。2026-09-18 決定)
 *  - 先頭の列を左に固定できる(sticky。width の指定が必要)
 *  - 行を押す(Enter/Space でも)と onRowClick。選択中の行は薄い青
 *  - スマホ(≤900px): mobile='stack' は表を縦積みに変形(既存方式)、'cards' は専用のカード一覧を出す。
 *    cards のときは表とカードの片方だけを描く(両方描いて CSS で隠すと DOM が倍になるため)
 *  - headerMenu(2026-10-14 追加): 見出しを押すと Excel のフィルタのようなメニュー(昇順・降順・解除と、列の filter での絞り込み)。
 *    絞り込み中は表の上に件数と「すべての絞り込みを解除」を出す
 */
import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { IconChevronLeft, IconChevronRight, IconFilter, IconSort, IconSortAsc, IconSortDesc } from '@core/components/icons';
import DataTableColumnMenu, { BLANK_KEY, type ColumnFilterState } from './DataTableColumnMenu';
import { useIsMobile } from '@core/lib/useIsMobile';

export type SortDirection = 'asc' | 'desc';
export type DataTableSort = { key: string; dir: SortDirection } | null;

export type DataTableColumn<T> = {
  key: string;
  header: ReactNode;
  /** 見出しの文字(スマホの縦積みの項目名)。header が文字なら省略可 */
  label?: string;
  /** 列幅(px)。sticky の列は必須 */
  width?: number;
  align?: 'left' | 'right' | 'center';
  /** 数値列(右揃え・等幅数字・折り返さない) */
  numeric?: boolean;
  /** 指定すると並べ替えできる。null/undefined は向きに関係なく最後 */
  sortValue?: (row: T) => string | number | null | undefined;
  defaultSortDir?: SortDirection;
  render: (row: T) => ReactNode;
  /** 行ごとにセルへ足すクラス(値の状態の色など) */
  cellClassName?: (row: T) => string | undefined;
  sticky?: boolean;
  /** 折り返さずに … で省略する */
  nowrap?: boolean;
  /** headerMenu のときの絞り込み: values=値の一覧にチェック / range=以上・以下(2026-10-14) */
  filter?: 'values' | 'range';
  /** 絞り込みに使う値(省略時は sortValue) */
  filterValue?: (row: T) => string | number | null | undefined;
  /** values の一覧に出す文字(省略時は値そのもの) */
  filterLabel?: (value: string | number) => string;
  /** range に足す区分のチェック(例: 目標達成/目標未達)。以上・以下と両方で絞れる。null の行は区分なし(2026-10-14) */
  filterGroup?: (row: T) => string | null;
};

const DEFAULT_WIDTH = 120;

function compareValues(a: string | number | null | undefined, b: string | number | null | undefined): number {
  const an = a === null || a === undefined || a === '';
  const bn = b === null || b === undefined || b === '';
  if (an || bn) return an === bn ? 0 : an ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'ja', { numeric: true });
}

export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  loading = false,
  emptyMessage = '該当するデータがありません',
  onRowClick,
  selectedKey = null,
  initialSort = null,
  pageSizes = [50, 100],
  defaultPageSize,
  compact = false,
  mobile = 'stack',
  renderMobileCard,
  maxHeight,
  headerMenu = false,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  caption?: string;
  loading?: boolean;
  emptyMessage?: ReactNode;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  initialSort?: DataTableSort;
  pageSizes?: number[];
  /** 最初の表示件数(省略時は pageSizes の先頭) */
  defaultPageSize?: number;
  compact?: boolean;
  mobile?: 'stack' | 'cards';
  renderMobileCard?: (row: T) => ReactNode;
  /** 表の枠の最大の高さ(見出しをこの枠の上端に固定する) */
  maxHeight?: string;
  /** 見出しを押すと並べ替え・絞り込みのメニューを開く(2026-10-14 追加) */
  headerMenu?: boolean;
}) {
  const [sort, setSort] = useState<DataTableSort>(initialSort);
  const [pageSize, setPageSize] = useState(defaultPageSize ?? pageSizes[0] ?? 50);
  const [page, setPage] = useState(0);
  const isMobile = useIsMobile();
  const showCards = mobile === 'cards' && !!renderMobileCard && isMobile;

  const [filters, setFilters] = useState<Record<string, ColumnFilterState>>({});
  const [menu, setMenu] = useState<{ key: string; el: HTMLElement } | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const valueOf = (c: DataTableColumn<T>, r: T) => (c.filterValue ?? c.sortValue)?.(r) ?? null;
  const keyOfValue = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? BLANK_KEY : String(v));
  const passes = useCallback(
    (r: T, skipKey?: string) =>
      Object.entries(filters).every(([key, f]) => {
        if (key === skipKey) return true;
        const c = columns.find((x) => x.key === key);
        if (!c) return true;
        const v = valueOf(c, r);
        if (f.kind === 'values') return !f.excluded.includes(keyOfValue(v));
        if (f.excludedGroups?.length && c.filterGroup && f.excludedGroups.includes(keyOfValue(c.filterGroup(r)))) return false;
        if (v === null || v === '' || typeof v !== 'number') return f.includeBlank;
        return (f.min === null || v >= f.min) && (f.max === null || v <= f.max);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, columns],
  );
  const filteredRows = useMemo(() => (Object.keys(filters).length ? rows.filter((r) => passes(r)) : rows), [rows, filters, passes]);
  const setFilter = (key: string, f: ColumnFilterState | null) => {
    setPage(0);
    setFilters((cur) => {
      const next = { ...cur };
      if (f) next[key] = f;
      else delete next[key];
      return next;
    });
  };

  const sorted = useMemo(() => {
    const rows = filteredRows;
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const withIndex = rows.map((r, i) => ({ r, i, v: col.sortValue!(r) }));
    withIndex.sort((x, y) => {
      const nx = x.v === null || x.v === undefined || x.v === '';
      const ny = y.v === null || y.v === undefined || y.v === '';
      if (nx || ny) return nx === ny ? x.i - y.i : nx ? 1 : -1;
      const c = compareValues(x.v, y.v);
      return (sort.dir === 'asc' ? c : -c) || x.i - y.i;
    });
    return withIndex.map((x) => x.r);
  }, [filteredRows, columns, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page > pageCount - 1) setPage(0);
  }, [page, pageCount]);
  const start = page * pageSize;
  const visible = sorted.slice(start, start + pageSize);

  const toggleSort = (col: DataTableColumn<T>) => {
    setPage(0);
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: col.defaultSortDir ?? (col.numeric ? 'desc' : 'asc') };
      if (prev.dir === (col.defaultSortDir ?? (col.numeric ? 'desc' : 'asc'))) return { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return null;
    });
  };

  // 左固定の列の位置(前の固定列の幅の合計)
  const stickyLeft = new Map<string, number>();
  {
    let left = 0;
    for (const c of columns) {
      if (!c.sticky) break;
      stickyLeft.set(c.key, left);
      left += c.width ?? DEFAULT_WIDTH;
    }
  }
  const lastSticky = [...stickyLeft.keys()].pop();
  const minWidth = columns.reduce((s, c) => s + (c.width ?? DEFAULT_WIDTH), 0);

  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (!onRowClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick(row);
    }
  };

  const labelOf = (c: DataTableColumn<T>) => c.label ?? (typeof c.header === 'string' ? c.header : '');
  const cellClass = (c: DataTableColumn<T>) =>
    [c.numeric ? 'is-numeric' : '', c.align === 'center' ? 'is-center' : c.align === 'right' ? 'is-right' : '', c.nowrap ? 'is-nowrap' : '', c.sticky ? 'is-sticky' : '', c.key === lastSticky ? 'is-sticky-edge' : '']
      .filter(Boolean)
      .join(' ');
  const stickyStyle = (c: DataTableColumn<T>) => (c.sticky ? { left: stickyLeft.get(c.key) } : undefined);

  const table = (
    <div className="dt-viewport" style={maxHeight ? { maxHeight } : undefined}>
      <table className={`data-table${compact ? ' is-compact' : ''}${mobile === 'cards' ? ' no-stack' : ''}`} style={{ minWidth }}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  className={cellClass(c)}
                  style={stickyStyle(c)}
                  aria-sort={c.sortValue ? (active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                >
                  {headerMenu && (c.sortValue || c.filter) ? (
                    <button
                      type="button"
                      className={`dt-sort${active ? ' is-active' : ''}${filters[c.key] ? ' is-filtered' : ''}`}
                      aria-haspopup="dialog"
                      aria-expanded={menu?.key === c.key}
                      onClick={(e) => setMenu(menu?.key === c.key ? null : { key: c.key, el: e.currentTarget })}
                    >
                      <span>{c.header}</span>
                      {filters[c.key] ? <IconFilter size={14} /> : active ? sort!.dir === 'asc' ? <IconSortAsc size={14} /> : <IconSortDesc size={14} /> : <IconSort size={14} />}
                    </button>
                  ) : c.sortValue ? (
                    <button type="button" className={`dt-sort${active ? ' is-active' : ''}`} onClick={() => toggleSort(c)}>
                      <span>{c.header}</span>
                      {active ? sort!.dir === 'asc' ? <IconSortAsc size={14} /> : <IconSortDesc size={14} /> : <IconSort size={14} />}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => {
            const key = rowKey(r);
            const selected = key === selectedKey;
            return (
              <tr
                key={key}
                className={`${onRowClick ? 'is-clickable' : ''}${selected ? ' is-selected' : ''}`}
                tabIndex={onRowClick ? 0 : undefined}
                aria-selected={onRowClick ? selected : undefined}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                onKeyDown={(e) => onRowKey(e, r)}
              >
                {columns.map((c) => (
                  <td key={c.key} className={[cellClass(c), c.cellClassName?.(r) ?? ''].filter(Boolean).join(' ') || undefined} style={stickyStyle(c)} data-label={labelOf(c) || undefined}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const menuCol = menu ? columns.find((c) => c.key === menu.key) : undefined;
  const menuOptions = useMemo(() => {
    const groupOf = menuCol?.filter === 'range' ? menuCol.filterGroup : undefined;
    if (!menuCol || (menuCol.filter !== 'values' && !groupOf)) return [];
    const counts = new Map<string, { value: string | number | null; count: number }>();
    for (const r of rows) {
      if (!passes(r, menuCol.key)) continue;
      const v = groupOf ? groupOf(r) : valueOf(menuCol, r);
      const k = keyOfValue(v);
      const cur = counts.get(k) ?? { value: v === undefined ? null : v, count: 0 };
      cur.count += 1;
      counts.set(k, cur);
    }
    return [...counts.entries()]
      .sort(([a], [b]) => (a === BLANK_KEY ? 1 : b === BLANK_KEY ? -1 : a.localeCompare(b, 'ja', { numeric: true })))
      .map(([key, { value, count }]) => ({ key, count, label: key === BLANK_KEY ? (groupOf ? '(区分なし)' : '(空白)') : !groupOf && menuCol.filterLabel ? menuCol.filterLabel(value as string | number) : key }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuCol, rows, passes]);
  const filterCount = Object.keys(filters).length;

  return (
    <div className={`data-table-wrap mobile-${mobile}`}>
      {filterCount > 0 && (
        <div className="dt-filter-bar">
          <IconFilter size={14} />
          <span>
            列で絞り込み中({filterCount}列) {filteredRows.length.toLocaleString('ja-JP')}件 / 全{rows.length.toLocaleString('ja-JP')}件
          </span>
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => (setFilters({}), setPage(0))}>
            すべての絞り込みを解除
          </button>
        </div>
      )}
      {menu && menuCol && (
        <DataTableColumnMenu
          anchor={menu.el}
          title={labelOf(menuCol) || menuCol.key}
          sortable={!!menuCol.sortValue}
          sortDir={sort?.key === menuCol.key ? sort.dir : null}
          onSort={(dir) => {
            setPage(0);
            setSort(dir ? { key: menuCol.key, dir } : null);
          }}
          filterKind={menuCol.filter ?? null}
          options={menuOptions}
          state={filters[menuCol.key] ?? null}
          onFilter={(f) => setFilter(menuCol.key, f)}
          onClose={closeMenu}
        />
      )}
      {loading ? (
        <div className="dt-empty">読み込み中…</div>
      ) : sorted.length === 0 ? (
        <div className="dt-empty">{emptyMessage}</div>
      ) : (
        <>
          {showCards ? (
            <ul className="dt-cards" aria-label={caption}>
              {visible.map((r) => {
                const key = rowKey(r);
                return (
                  <li key={key}>
                    <button type="button" className={`dt-card${key === selectedKey ? ' is-selected' : ''}`} onClick={onRowClick ? () => onRowClick(r) : undefined}>
                      {renderMobileCard!(r)}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="dt-desktop">{table}</div>
          )}
          {/* 〜50件はページ送りを出さない(設計書 §4-5) */}
          {sorted.length > Math.min(...pageSizes) && (
          <div className="dt-footer">
            <span className="dt-range">
              {sorted.length > 0 ? `${(start + 1).toLocaleString('ja-JP')}–${Math.min(start + pageSize, sorted.length).toLocaleString('ja-JP')}件` : '0件'} / 全{sorted.length.toLocaleString('ja-JP')}件
            </span>
            <label className="dt-page-size">
              表示件数
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
              >
                {pageSizes.map((n) => (
                  <option key={n} value={n}>
                    {n}件
                  </option>
                ))}
              </select>
            </label>
            {pageCount > 1 && (
              <span className="dt-pager">
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label="前のページ">
                  <IconChevronLeft size={16} />
                </button>
                <span className="dt-page-no">
                  {page + 1} / {pageCount}
                </span>
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1} aria-label="次のページ">
                  <IconChevronRight size={16} />
                </button>
              </span>
            )}
          </div>
          )}
        </>
      )}
    </div>
  );
}
